import { Controller } from "@hotwired/stimulus";

/**
 * Restaurant Items Modal Controller
 *
 * Handles validation of modifier selections in the restaurant items modal:
 * - Prevents selecting more modifiers than allowed by the modifier group
 * - Dynamically manages HTML5 validation based on selection state
 * - Disables options when maximum selections are reached
 * - Handles quantity adjustments for modifiers with max > 1
 */
export default class extends Controller {
  static targets = [
    "modifierGroup",
    "modifierCheckbox",
    "modifierRadio",
    "quantityInput",
    "mainQuantityInput",
  ];

  connect() {
    // Initialize validation state for each modifier group
    this.modifierGroupTargets.forEach((group) => {
      this.validateGroup(group);
      this.updateValidationState(group);
    });
  }

  /**
   * Validates modifier selection when a checkbox is clicked
   * @param {Event} event - The click event
   */
  validateCheckbox(event) {
    const checkbox = event.target;
    const group = checkbox.closest(
      "[data-restaurant-items-modal-target='modifierGroup']"
    );

    if (checkbox.checked) {
      const selectedCount = this.getSelectedCount(group);
      const maxSelections = parseInt(group.dataset.maxSelections);

      if (selectedCount > maxSelections) {
        event.preventDefault();
        checkbox.checked = false;
      }
    }

    // Update validation state after checkbox change
    this.updateValidationState(group);
  }

  /**
   * Validates the form before submission
   * @param {Event} event - The submit event
   */
  validateForm(event) {
    let isValid = true;

    this.modifierGroupTargets.forEach((group) => {
      const selectedCount = this.getSelectedCount(group);
      const minSelections = parseInt(group.dataset.minSelections);

      if (selectedCount < minSelections) {
        isValid = false;
      }
    });

    if (!isValid) {
      event.preventDefault();
    }
  }

  /**
   * Gets the count of selected modifiers in a group
   * @param {HTMLElement} group - The modifier group element
   * @returns {number} The count of selected modifiers
   */
  getSelectedCount(group) {
    const checkboxes = group.querySelectorAll(
      "[data-restaurant-items-modal-target='modifierCheckbox']:checked"
    );
    const radios = group.querySelectorAll(
      "[data-restaurant-items-modal-target='modifierRadio']:checked"
    );
    const quantities = group.querySelectorAll(
      "[data-restaurant-items-modal-target='quantityInput']"
    );

    let count = checkboxes.length + radios.length;

    // Count quantity inputs that have a value greater than 0
    quantities.forEach((input) => {
      const quantity = parseInt(input.value) || 0;
      if (quantity > 0) {
        count += 1; // Count as one selection if quantity > 0
      }
    });

    return count;
  }

  /**
   * Gets the total quantity of a modifier
   * @param {HTMLElement} group - The modifier group element
   * @param {string} modifierId - The ID of the modifier
   * @returns {number} The total quantity of the modifier
   */
  getModifierQuantity(group, modifierId) {
    const input = group.querySelector(
      `[data-restaurant-items-modal-target='quantityInput'][data-restaurant-items-modal-modifier-id='${modifierId}']`
    );
    return input ? parseInt(input.value) || 0 : 0;
  }

  /**
   * Validates a modifier group's selections
   * @param {HTMLElement} group - The modifier group element
   */
  validateGroup(group) {
    const selectedCount = this.getSelectedCount(group);
    const maxSelections =
      parseInt(group.dataset.maxSelections);

    if (selectedCount > maxSelections) {
      let remaining = selectedCount - maxSelections;

      // Uncheck excess checkboxes first
      const checkboxes = group.querySelectorAll(
        `[data-restaurant-items-modal-target='modifierCheckbox']:checked`
      );
      for (
        let i = 0;
        i < checkboxes.length && remaining > 0;
        i++
      ) {
        checkboxes[i].checked = false;
        remaining--;
      }

      // Reset quantity inputs if still over the limit
      if (remaining > 0) {
        const quantities = group.querySelectorAll(
          `[data-restaurant-items-modal-target='quantityInput']`
        );
        for (const input of quantities) {
          if (remaining <= 0) break;
          if ((parseInt(input.value) || 0) > 0) {
            input.value = 0;
            remaining--;
          }
        }
      }
    }
  }

  /**
   * Updates the HTML5 validation state for a modifier group
   * @param {HTMLElement} group - The modifier group element
   */
  updateValidationState(group) {
    const selectedCount = this.getSelectedCount(group);
    const minSelections = parseInt(group.dataset.minSelections);
    const maxSelections = parseInt(group.dataset.maxSelections);

    // Get all inputs in the group
    const inputs = group.querySelectorAll(
      'input[type="checkbox"], input[type="radio"]'
    );

    inputs.forEach((input) => {
      // If we've met the minimum requirements, remove required attribute
      if (selectedCount >= minSelections) {
        input.removeAttribute("required");
      } else {
        // If we haven't met minimum requirements, add required attribute
        input.setAttribute("required", "");
      }

      // For checkboxes, if we've reached max selections, disable unchecked ones
      if (input.type === "checkbox" && !input.checked) {
        if (selectedCount >= maxSelections) {
          input.disabled = true;
        } else {
          input.disabled = false;
        }
      }
    });

    // Handle quantity inputs
    const quantityInputs = group.querySelectorAll(
      "[data-restaurant-items-modal-target='quantityInput']"
    );
    const quantityButtons = group.querySelectorAll(
      "[data-action*='restaurant-items-modal#incrementQuantity'], [data-action*='restaurant-items-modal#decrementQuantity']"
    );

    quantityInputs.forEach((input) => {
      const currentQuantity = parseInt(input.value) || 0;
      const maxValue = parseInt(input.dataset.restaurantItemsModalMaxValue);
      const modifierId = input.dataset.restaurantItemsModalModifierId;

      // Find the increment button for this input
      const incrementButton = Array.from(quantityButtons).find(
        (button) =>
          button.dataset.restaurantItemsModalModifierId === modifierId &&
          button.dataset.action.includes("incrementQuantity")
      );

      // If we've reached max selections and this input has no quantity,
      // disable the increment button
      if (selectedCount >= maxSelections && currentQuantity === 0) {
        if (incrementButton) {
          incrementButton.disabled = true;
        }
      } else {
        if (incrementButton) {
          incrementButton.disabled = false;
        }
      }
    });
  }

  /**
   * Increments the quantity for a modifier
   * @param {Event} event - The click event
   */
  incrementQuantity(event) {
    const button = event.currentTarget;
    const modifierId = button.dataset.restaurantItemsModalModifierId;
    const maxValue = parseInt(button.dataset.restaurantItemsModalMaxValue);
    const input = this.quantityInputTargets.find(
      (input) => input.dataset.restaurantItemsModalModifierId === modifierId
    );

    if (input) {
      const currentValue = parseInt(input.value) || 0;
      const newValue = Math.min(currentValue + 1, maxValue);
      input.value = newValue;
      this.validateQuantity({ target: input });
    }
  }

  /**
   * Decrements the quantity for a modifier
   * @param {Event} event - The click event
   */
  decrementQuantity(event) {
    const button = event.currentTarget;
    const modifierId = button.dataset.restaurantItemsModalModifierId;
    const input = this.quantityInputTargets.find(
      (input) => input.dataset.restaurantItemsModalModifierId === modifierId
    );

    if (input) {
      const currentValue = parseInt(input.value) || 0;
      const newValue = Math.max(currentValue - 1, 0);
      input.value = newValue;
      this.validateQuantity({ target: input });
    }
  }

  /**
   * Validates the quantity input for a modifier
   * @param {Event} event - The input event
   */
  validateQuantity(event) {
    const input = event.target;
    const group = input.closest(
      "[data-restaurant-items-modal-target='modifierGroup']"
    );
    const maxValue = parseInt(input.dataset.restaurantItemsModalMaxValue);
    const currentValue = parseInt(input.value) || 0;

    // Ensure the value is within the modifier's max limit
    if (currentValue > maxValue) {
      input.value = maxValue;
    }

    // Ensure the value is not negative
    if (currentValue < 0) {
      input.value = 0;
    }

    // Update validation state for the group
    this.updateValidationState(group);
  }

  /**
   * Increments the main item quantity
   * @param {Event} event - The click event
   */
  incrementMainQuantity(event) {
    event.preventDefault();
    if (this.hasMainQuantityInputTarget) {
      const currentValue = parseInt(this.mainQuantityInputTarget.value) || 1;
      const maxValue = parseInt(this.mainQuantityInputTarget.max) || 99;
      const newValue = Math.min(currentValue + 1, maxValue);
      this.mainQuantityInputTarget.value = newValue;
    }
  }

  /**
   * Decrements the main item quantity
   * @param {Event} event - The click event
   */
  decrementMainQuantity(event) {
    event.preventDefault();
    if (this.hasMainQuantityInputTarget) {
      const currentValue = parseInt(this.mainQuantityInputTarget.value) || 1;
      const minValue = parseInt(this.mainQuantityInputTarget.min) || 1;
      const newValue = Math.max(currentValue - 1, minValue);
      this.mainQuantityInputTarget.value = newValue;
    }
  }

  /**
   * Validates the main quantity input
   * @param {Event} event - The input event
   */
  validateMainQuantity(event) {
    const input = event.target;
    const currentValue = parseInt(input.value) || 1;
    const minValue = parseInt(input.min) || 1;
    const maxValue = parseInt(input.max) || 99;

    if (currentValue < minValue) {
      input.value = minValue;
    } else if (currentValue > maxValue) {
      input.value = maxValue;
    }
  }
}
